import React, { useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Eye, EyeOff, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatINR, formatNum } from "@/lib/api";

const Pill = ({ children, color }) => (
  <span
    className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
    style={{ background: `${color}1A`, color: color }}
  >
    {children}
  </span>
);

const typeColor = {
  B2C: "#1F3A5F",
  B2B: "#E76F1A",
  B2B2C: "#2BA88A",
};

const kindBadge = {
  student: { label: "Student", color: "#5B7B9A" },
  partnership: { label: "Partner", color: "#7A4E8A" },
};

export const Leaderboard = ({
  rows,
  total,
  page,
  pageSize,
  loading,
  sort,
  onSort,
  onPage,
}) => {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const startIdx = (page - 1) * pageSize;

  return (
    <div data-testid="leaderboard">
      <div className="overflow-x-auto" data-testid="leaderboard-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border">
              <th className="py-2 pr-3 text-[11px] uppercase tracking-widest text-muted-foreground">#</th>
              <th className="py-2 pr-3">
                <SortableHeader id="name" label="Sales Person" currentSort={sort} onSort={onSort} />
              </th>
              <th className="py-2 pr-3 text-[11px] uppercase tracking-widest text-muted-foreground">
                Designation
              </th>
              <th className="py-2 pr-3 text-right">
                <SortableHeader id="revenue" label="Revenue" currentSort={sort} onSort={onSort} align="right" />
              </th>
              <th className="py-2 pr-3 text-right">
                <SortableHeader id="collection" label="Collection" currentSort={sort} onSort={onSort} align="right" />
              </th>
              <th className="py-2 pr-3 text-right">
                <SortableHeader id="students" label="Students" currentSort={sort} onSort={onSort} align="right" />
              </th>
              <th className="py-2 text-right">
                <SortableHeader id="avg_deal_size" label="Avg deal" currentSort={sort} onSort={onSort} align="right" />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="py-8 text-center text-muted-foreground text-xs">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 text-center text-muted-foreground">
                  No data for current filters.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr
                key={r.id}
                className="border-b border-border/60 last:border-0 hover:bg-secondary/40 transition-colors"
              >
                <td className="py-3 pr-3 font-display text-base font-semibold text-muted-foreground tabular">
                  {String(startIdx + i + 1).padStart(2, "0")}
                </td>
                <td className="py-3 pr-3 font-semibold">{r.name}</td>
                <td className="py-3 pr-3 text-muted-foreground">{r.designation || "—"}</td>
                <td className="py-3 pr-3 text-right tabular font-semibold">
                  {formatINR(r.revenue)}
                </td>
                <td className="py-3 pr-3 text-right tabular text-muted-foreground">
                  {formatINR(r.collection)}
                </td>
                <td className="py-3 pr-3 text-right tabular">{formatNum(r.students)}</td>
                <td className="py-3 text-right tabular text-muted-foreground">
                  {formatINR(r.avg_deal_size)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-border/60">
        <div className="text-xs text-muted-foreground tabular">
          Page {page} of {totalPages}
          {" · "}
          {formatNum(total)} sales {total === 1 ? "person" : "people"}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => onPage(page - 1)}
            className="h-8 px-3 text-xs gap-1"
            data-testid="leaderboard-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => onPage(page + 1)}
            className="h-8 px-3 text-xs gap-1"
            data-testid="leaderboard-next"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const maskEmail = (email) => {
  if (!email) return "—";
  const [user, domain] = String(email).split("@");
  if (!domain) return "—";
  const visible = user.slice(0, Math.min(2, user.length));
  return `${visible}${"•".repeat(Math.max(3, user.length - 2))}@${domain}`;
};

const maskPhone = (phone) => {
  if (!phone) return "—";
  const s = String(phone).replace(/\s+/g, "");
  if (s.length < 4) return "•".repeat(s.length);
  return `${"•".repeat(s.length - 4)}${s.slice(-4)}`;
};

const sortIconFor = (active, dir) => {
  if (!active) return ArrowUpDown;
  return dir === "asc" ? ArrowUp : ArrowDown;
};

const nextSortDir = (active, dir) => {
  if (!active) return "desc";
  return dir === "desc" ? "asc" : "desc";
};

const SortableHeader = ({ id, label, currentSort, onSort, align = "left" }) => {
  const active = currentSort.by === id;
  const Icon = sortIconFor(active, currentSort.dir);
  const next = nextSortDir(active, currentSort.dir);
  return (
    <button
      type="button"
      onClick={() => onSort({ by: id, dir: next })}
      className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold transition-colors ${
        active ? "text-[#1F3A5F]" : "text-muted-foreground hover:text-foreground"
      } ${align === "right" ? "justify-end w-full" : ""}`}
      data-testid={`sort-${id}`}
    >
      <span>{label}</span>
      <Icon className="w-3 h-3 opacity-70" />
    </button>
  );
};

export const RecentEnrolmentsTable = ({
  rows,
  total,
  page,
  pageSize,
  loading,
  search,
  onSearch,
  sort,
  onSort,
  onPage,
}) => {
  const [revealed, setRevealed] = useState(false);
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  return (
    <div data-testid="recent-enrolments">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search name, email, or phone…"
            className="h-9 pl-9 text-sm"
            data-testid="student-search"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setRevealed((v) => !v)}
          className="h-9 gap-2 text-xs"
          data-testid="toggle-contact"
        >
          {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {revealed ? "Hide contact" : "Reveal contact"}
        </Button>
        <div className="text-xs text-muted-foreground tabular ml-auto">
          {formatNum(total)} matching
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border">
              <th className="py-2 pr-3">
                <SortableHeader id="name" label="Student / Partner" currentSort={sort} onSort={onSort} />
              </th>
              <th className="py-2 pr-3">
                <SortableHeader id="kind" label="Kind" currentSort={sort} onSort={onSort} />
              </th>
              <th className="py-2 pr-3">
                <SortableHeader id="client_type" label="Type" currentSort={sort} onSort={onSort} />
              </th>
              <th className="py-2 pr-3">Program</th>
              <th className="py-2 pr-3">Sales Person</th>
              <th className="py-2 pr-3">
                <SortableHeader id="enrolment_date" label="Enrolled" currentSort={sort} onSort={onSort} />
              </th>
              <th className="py-2 pr-3 text-right">
                <SortableHeader id="total_revenue" label="Revenue" currentSort={sort} onSort={onSort} align="right" />
              </th>
              <th className="py-2 text-right">
                <SortableHeader id="outstanding" label="Outstanding" currentSort={sort} onSort={onSort} align="right" />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="py-8 text-center text-muted-foreground text-xs">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="py-8 text-center text-muted-foreground">
                  No enrolments match your search.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const kind = kindBadge[r.kind] || kindBadge.student;
              return (
                <tr
                  key={`${r.kind}-${r.id}`}
                  className="border-b border-border/60 last:border-0 hover:bg-secondary/40 transition-colors"
                >
                  <td className="py-3 pr-3">
                    <div className="font-semibold">{r.name || "—"}</div>
                    {r.contact_person && (
                      <div className="text-[11px] text-muted-foreground">
                        Contact · {r.contact_person}
                      </div>
                    )}
                    <div
                      className={`text-xs tabular ${revealed ? "text-muted-foreground" : "text-muted-foreground/70 select-none"}`}
                      title={revealed ? "" : "Hidden — click Reveal contact"}
                    >
                      {revealed ? r.email || "—" : maskEmail(r.email)}
                      {" · "}
                      {revealed ? r.phone || "—" : maskPhone(r.phone)}
                    </div>
                  </td>
                  <td className="py-3 pr-3">
                    <Pill color={kind.color}>{kind.label}</Pill>
                  </td>
                  <td className="py-3 pr-3">
                    {r.client_type ? (
                      <Pill color={typeColor[r.client_type] || "#64748b"}>
                        {r.client_type}
                      </Pill>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-3 pr-3 text-muted-foreground text-xs">
                    {r.program?.join(", ") || "—"}
                  </td>
                  <td className="py-3 pr-3 text-xs">{r.sales_person?.join(", ") || "—"}</td>
                  <td className="py-3 pr-3 tabular text-muted-foreground text-xs">
                    {r.enrolment_date || "—"}
                  </td>
                  <td className="py-3 pr-3 text-right tabular font-semibold">
                    {formatINR(r.total_revenue)}
                  </td>
                  <td
                    className={`py-3 text-right tabular ${
                      r.outstanding > 0 ? "text-[#B23A48]" : "text-[#2BA88A]"
                    }`}
                  >
                    {formatINR(r.outstanding)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-border/60">
        <div className="text-xs text-muted-foreground tabular">
          Page {page} of {totalPages}
          {" · "}
          {formatNum(Math.min(page * pageSize, total || 0))} of {formatNum(total)}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => onPage(page - 1)}
            className="h-8 px-3 text-xs gap-1"
            data-testid="pagination-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => onPage(page + 1)}
            className="h-8 px-3 text-xs gap-1"
            data-testid="pagination-next"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};


const paymentStatusColor = {
  Paid: "#2BA88A",
  Pending: "#E76F1A",
  Overdue: "#B23A48",
  Failed: "#B23A48",
  Refunded: "#7A4E8A",
};

export const RecentPaymentsTable = ({
  rows,
  total,
  totalAmount,
  page,
  pageSize,
  loading,
  search,
  onSearch,
  sort,
  onSort,
  onPage,
}) => {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  return (
    <div data-testid="recent-payments">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search payer, program, status, transaction…"
            className="h-9 pl-9 text-sm"
            data-testid="payment-search"
          />
        </div>
        <div className="text-xs text-muted-foreground tabular ml-auto">
          {formatNum(total)} payments · {formatINR(totalAmount)}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border">
              <th className="py-2 pr-3">
                <SortableHeader id="payment_date" label="Date" currentSort={sort} onSort={onSort} />
              </th>
              <th className="py-2 pr-3">
                <SortableHeader id="payer_name" label="Payer" currentSort={sort} onSort={onSort} />
              </th>
              <th className="py-2 pr-3 text-[11px] uppercase tracking-widest text-muted-foreground">
                Program
              </th>
              <th className="py-2 pr-3 text-[11px] uppercase tracking-widest text-muted-foreground">
                Sales Person
              </th>
              <th className="py-2 pr-3">
                <SortableHeader id="payment_status" label="Status" currentSort={sort} onSort={onSort} />
              </th>
              <th className="py-2 text-right">
                <SortableHeader id="amount_paid" label="Amount" currentSort={sort} onSort={onSort} align="right" />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-muted-foreground text-xs">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-8 text-center text-muted-foreground">
                  No payments match your search.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const kind = kindBadge[r.payer_kind];
              return (
                <tr
                  key={r.id}
                  className="border-b border-border/60 last:border-0 hover:bg-secondary/40 transition-colors"
                >
                  <td className="py-3 pr-3 tabular text-muted-foreground text-xs whitespace-nowrap">
                    {r.payment_date || "—"}
                  </td>
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" title={r.payer_name}>
                        {r.payer_name || "—"}
                      </span>
                      {kind && <Pill color={kind.color}>{kind.label}</Pill>}
                    </div>
                    {r.transaction_id && (
                      <div
                        className="text-[10px] text-muted-foreground tabular truncate"
                        title={r.transaction_id}
                      >
                        {r.transaction_id}
                      </div>
                    )}
                  </td>
                  <td className="py-3 pr-3 text-muted-foreground text-xs">
                    {r.program?.join(", ") || "—"}
                  </td>
                  <td className="py-3 pr-3 text-xs">
                    {r.sales_person?.join(", ") || "—"}
                  </td>
                  <td className="py-3 pr-3">
                    {r.payment_status ? (
                      <Pill
                        color={paymentStatusColor[r.payment_status] || "#64748b"}
                      >
                        {r.payment_status}
                      </Pill>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-3 text-right tabular font-semibold">
                    {formatINR(r.amount_paid)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-border/60">
        <div className="text-xs text-muted-foreground tabular">
          Page {page} of {totalPages} · {formatNum(Math.min(page * pageSize, total || 0))} of {formatNum(total)}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => onPage(page - 1)}
            className="h-8 px-3 text-xs gap-1"
            data-testid="payments-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => onPage(page + 1)}
            className="h-8 px-3 text-xs gap-1"
            data-testid="payments-next"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
